// src/utils/repeatAnalyzer.js - Analyzes musical repeats and endings from OSMD data

export class RepeatAnalyzer {
  /**
   * Analyze repeats and endings in an OSMD score
   * @param {Object} osmd - OSMD instance with loaded score
   * @returns {Object} - Repeat analysis result
   */
  static analyzeRepeats(osmd) {
    if (!osmd?.Sheet?.sourceMeasures || !osmd.Sheet.sourceMeasures.length) {
      return {
        hasRepeats: false,
        playbackOrder: null,
        repeatStructures: [],
        endings: []
      };
    }

    const measureCount = osmd.Sheet.sourceMeasures.length;
    const repeatStructures = [];
    const endings = [];
    let playbackOrder = [];

    // First try to extract repeat information from OSMD's parsed structures
    const osmdRepeatInfo = this.extractRepeatsFromOSMD(osmd);
    
    if (osmdRepeatInfo.hasRepeats) {
      return osmdRepeatInfo;
    }
    
    // If OSMD parsing fails, try raw XML as fallback
    const repeatInfo = this.parseRawMusicXML(osmd);
    
    if (repeatInfo.hasRepeats) {
      return repeatInfo;
    }

    // First pass: identify all repeats and endings
    for (let measureIndex = 0; measureIndex < measureCount; measureIndex++) {
      const measure = osmd.Sheet.sourceMeasures[measureIndex];
      const measureNumber = measure.MeasureNumber || measureIndex + 1;

      // Check for repeat and ending markers in this measure
      const repeatInfo = this.extractRepeatInfo(measure, measureNumber);
      
      if (repeatInfo.hasForwardRepeat) {
        repeatStructures.push({
          type: 'forward_repeat',
          measureNumber: measureNumber,
          measureIndex: measureIndex
        });
      }

      if (repeatInfo.hasBackwardRepeat) {
        repeatStructures.push({
          type: 'backward_repeat',
          measureNumber: measureNumber,
          measureIndex: measureIndex,
          endingNumber: repeatInfo.endingNumber
        });
      }

      if (repeatInfo.hasEnding) {
        endings.push({
          endingNumber: repeatInfo.endingNumber,
          type: repeatInfo.endingType, // 'start' or 'stop'
          measureNumber: measureNumber,
          measureIndex: measureIndex
        });
      }
    }

    // Second pass: build playback order
    if (repeatStructures.length > 0 || endings.length > 0) {
      playbackOrder = this.buildPlaybackOrder(measureCount, repeatStructures, endings, osmd);
    }

    const hasRepeats = repeatStructures.length > 0 || endings.length > 0;
    
    const result = {
      hasRepeats,
      playbackOrder: hasRepeats ? playbackOrder : null,
      repeatStructures,
      endings,
      totalMeasures: measureCount
    };
    
    return result;
  }

  /**
   * Extract repeat and ending information from a single measure
   * @param {Object} measure - OSMD measure object
   * @param {number} measureNumber - Measure number
   * @returns {Object} - Repeat info for this measure
   */
  static extractRepeatInfo(measure, measureNumber) {
    let hasForwardRepeat = false;
    let hasBackwardRepeat = false;
    let hasEnding = false;
    let endingNumber = null;
    let endingType = null;

    // Check OSMD's repetition instructions
    if (measure.firstRepetitionInstructions && measure.firstRepetitionInstructions.length > 0) {
      for (const instruction of measure.firstRepetitionInstructions) {
        // Check for forward repeats and endings
        if (instruction.type === 'Repetition' || instruction.constructor.name.includes('Repetition')) {
          if (instruction.startMarker || instruction.backward === false) {
            hasForwardRepeat = true;
          }
        }
        if (instruction.type === 'Ending' || instruction.constructor.name.includes('Ending')) {
          hasEnding = true;
          endingNumber = instruction.endingNumbers?.[0] || instruction.number || 1;
          endingType = 'start';
        }
      }
    }

    if (measure.lastRepetitionInstructions && measure.lastRepetitionInstructions.length > 0) {
      for (const instruction of measure.lastRepetitionInstructions) {
        // Check for backward repeats and ending stops
        if (instruction.type === 'Repetition' || instruction.constructor.name.includes('Repetition')) {
          if (instruction.endMarker || instruction.backward === true) {
            hasBackwardRepeat = true;
          }
        }
        if (instruction.type === 'Ending' || instruction.constructor.name.includes('Ending')) {
          hasEnding = true;
          endingNumber = endingNumber || instruction.endingNumbers?.[0] || instruction.number || 1;
          endingType = 'stop';
        }
      }
    }

    // Check for barlines with repeat markings
    if (measure.staffEntries) {
      for (const staffEntry of measure.staffEntries) {
        // Check instructions for repeat markings
        if (staffEntry.instructions) {
          for (const instruction of staffEntry.instructions) {
            // Look for repeat instructions
            if (instruction.Repeat) {
              if (instruction.Repeat === 'Forward') {
                hasForwardRepeat = true;
              } else if (instruction.Repeat === 'Backward') {
                hasBackwardRepeat = true;
              }
            }

            // Look for ending markings
            if (instruction.Ending) {
              hasEnding = true;
              endingNumber = instruction.Ending.Number || instruction.Ending;
              endingType = instruction.Ending.Type || 'start';
            }
          }
        }

        // Also check for barlines in the staff entry
        if (staffEntry.graphicalStaffEntry) {
          const barlines = this.findBarlines(staffEntry.graphicalStaffEntry);
          const barlineInfo = this.analyzeBarlines(barlines);
          
          hasForwardRepeat = hasForwardRepeat || barlineInfo.hasForwardRepeat;
          hasBackwardRepeat = hasBackwardRepeat || barlineInfo.hasBackwardRepeat;
          
          if (barlineInfo.hasEnding) {
            hasEnding = true;
            endingNumber = endingNumber || barlineInfo.endingNumber;
            endingType = endingType || barlineInfo.endingType;
          }
        }
      }
    }

    // Also check the measure's beginning and end barlines directly
    const measureBarlineInfo = this.analyzeMeasureBarlines(measure);
    hasForwardRepeat = hasForwardRepeat || measureBarlineInfo.hasForwardRepeat;
    hasBackwardRepeat = hasBackwardRepeat || measureBarlineInfo.hasBackwardRepeat;
    
    if (measureBarlineInfo.hasEnding) {
      hasEnding = true;
      endingNumber = endingNumber || measureBarlineInfo.endingNumber;
      endingType = endingType || measureBarlineInfo.endingType;
    }

    return {
      hasForwardRepeat,
      hasBackwardRepeat,
      hasEnding,
      endingNumber,
      endingType
    };
  }

  /**
   * Find barlines in a graphical staff entry
   * @param {Object} graphicalStaffEntry - OSMD graphical staff entry
   * @returns {Array} - Array of barline objects
   */
  static findBarlines(graphicalStaffEntry) {
    const barlines = [];
    
    // Check various properties where barlines might be stored
    if (graphicalStaffEntry.staffEntryParent?.barlineAtBeginning) {
      barlines.push(graphicalStaffEntry.staffEntryParent.barlineAtBeginning);
    }
    
    if (graphicalStaffEntry.staffEntryParent?.barlineAtEnd) {
      barlines.push(graphicalStaffEntry.staffEntryParent.barlineAtEnd);
    }

    // Also check the measure's barlines
    if (graphicalStaffEntry.parentMeasure?.beginningBarline) {
      barlines.push(graphicalStaffEntry.parentMeasure.beginningBarline);
    }
    
    if (graphicalStaffEntry.parentMeasure?.endingBarline) {
      barlines.push(graphicalStaffEntry.parentMeasure.endingBarline);
    }

    return barlines;
  }

  /**
   * Analyze measure-level barlines
   * @param {Object} measure - OSMD measure object  
   * @returns {Object} - Barline analysis
   */
  static analyzeMeasureBarlines(measure) {
    let hasForwardRepeat = false;
    let hasBackwardRepeat = false;
    let hasEnding = false;
    let endingNumber = null;
    let endingType = null;

    // Check beginning barline
    if (measure.beginningBarline) {
      const beginInfo = this.analyzeBarlines([measure.beginningBarline]);
      hasForwardRepeat = hasForwardRepeat || beginInfo.hasForwardRepeat;
      hasEnding = hasEnding || beginInfo.hasEnding;
      endingNumber = endingNumber || beginInfo.endingNumber;
      endingType = endingType || beginInfo.endingType;
    }

    // Check ending barline
    if (measure.endingBarline) {
      const endInfo = this.analyzeBarlines([measure.endingBarline]);
      hasBackwardRepeat = hasBackwardRepeat || endInfo.hasBackwardRepeat;
      hasEnding = hasEnding || endInfo.hasEnding;
      endingNumber = endingNumber || endInfo.endingNumber;
      endingType = endingType || endInfo.endingType;
    }

    return {
      hasForwardRepeat,
      hasBackwardRepeat,
      hasEnding,
      endingNumber,
      endingType
    };
  }

  /**
   * Analyze barline objects for repeat and ending information
   * @param {Array} barlines - Array of barline objects
   * @returns {Object} - Analysis result
   */
  static analyzeBarlines(barlines) {
    let hasForwardRepeat = false;
    let hasBackwardRepeat = false;
    let hasEnding = false;
    let endingNumber = null;
    let endingType = null;

    for (const barline of barlines) {
      if (!barline) continue;

      // Check for repeat markings
      if (barline.Repeat) {
        if (barline.Repeat === 'Forward' || barline.Repeat === 'forward') {
          hasForwardRepeat = true;
        } else if (barline.Repeat === 'Backward' || barline.Repeat === 'backward') {
          hasBackwardRepeat = true;
        }
      }

      // Check for ending markings
      if (barline.Ending) {
        hasEnding = true;
        if (typeof barline.Ending === 'object') {
          endingNumber = barline.Ending.Number || barline.Ending.number;
          endingType = barline.Ending.Type || barline.Ending.type || 'start';
        } else {
          endingNumber = barline.Ending;
          endingType = 'start';
        }
      }

      // Also check string-based properties that might contain repeat info
      if (typeof barline === 'object') {
        for (const [key, value] of Object.entries(barline)) {
          const keyLower = key.toLowerCase();
          const valueLower = typeof value === 'string' ? value.toLowerCase() : '';
          
          if (keyLower.includes('repeat')) {
            if (valueLower.includes('forward') || valueLower.includes('start')) {
              hasForwardRepeat = true;
            } else if (valueLower.includes('backward') || valueLower.includes('end')) {
              hasBackwardRepeat = true;
            }
          }
          
          if (keyLower.includes('ending')) {
            hasEnding = true;
            // Try to extract ending number
            const numberMatch = valueLower.match(/(\d+)/);
            if (numberMatch) {
              endingNumber = parseInt(numberMatch[1]);
            }
          }
        }
      }
    }

    return {
      hasForwardRepeat,
      hasBackwardRepeat,
      hasEnding,
      endingNumber,
      endingType
    };
  }

  /**
   * Build ending ranges from start/stop markers
   * @param {Array} endings - Array of ending markers with start/stop types
   * @returns {Array} - Array of ending ranges with {endingNumber, startMeasure, endMeasure}
   */
  static buildEndingRanges(endings) {
    const ranges = [];
    const endingsByNumber = {}; // Group by ending number

    endings.forEach(e => {
      if (!endingsByNumber[e.endingNumber]) {
        endingsByNumber[e.endingNumber] = [];
      }
      endingsByNumber[e.endingNumber].push(e);
    });

    Object.entries(endingsByNumber).forEach(([number, markers]) => {
      const starts = markers.filter(m => m.type === 'start');
      const stops = markers.filter(m => m.type === 'stop');

      if (starts.length === 1 && stops.length === 1) {
        // Normal case: one start, one stop - create range
        ranges.push({
          endingNumber: parseInt(number),
          startMeasure: starts[0].measureNumber,
          endMeasure: stops[0].measureNumber
        });
      } else if (starts.length === 1 && stops.length === 0) {
        // Only start marker - treat as single measure (or use start as both start and end)
        ranges.push({
          endingNumber: parseInt(number),
          startMeasure: starts[0].measureNumber,
          endMeasure: starts[0].measureNumber
        });
      } else if (starts.length === 0 && stops.length === 1) {
        // Only stop marker - treat as single measure
        ranges.push({
          endingNumber: parseInt(number),
          startMeasure: stops[0].measureNumber,
          endMeasure: stops[0].measureNumber
        });
      } else if (markers.length === 1) {
        // Single marker with no type specified - treat as single measure
        ranges.push({
          endingNumber: parseInt(number),
          startMeasure: markers[0].measureNumber,
          endMeasure: markers[0].measureNumber
        });
      } else {
        // Fallback: multiple starts or stops - treat each marker as individual measure
        markers.forEach(m => {
          ranges.push({
            endingNumber: parseInt(number),
            startMeasure: m.measureNumber,
            endMeasure: m.measureNumber
          });
        });
      }
    });

    return ranges;
  }

  /**
   * Build the playback order for measures considering repeats and endings
   * @param {number} totalMeasures - Total number of measures
   * @param {Array} repeatStructures - Array of repeat markers
   * @param {Array} endings - Array of ending markers
   * @returns {Array} - Array of measure numbers in playback order
   */
  static buildPlaybackOrder(totalMeasures, repeatStructures, endings, osmd = null) {
    const playbackOrder = [];

    // Sort structures by measure number
    const sortedRepeats = [...repeatStructures].sort((a, b) => a.measureNumber - b.measureNumber);
    const sortedEndings = [...endings].sort((a, b) => a.measureNumber - b.measureNumber);

    // Build ending ranges from start/stop markers
    const endingRanges = this.buildEndingRanges(sortedEndings);

    // Detect pickup measure: Check if measure 1 (or 0) is a partial measure
    let pickupMeasureNumber = null;
    if (osmd?.Sheet?.sourceMeasures) {
      // Check measure at index 0 (first measure)
      const firstMeasure = osmd.Sheet.sourceMeasures[0];
      if (firstMeasure) {
        // Check if this measure has a partial duration (indicating pickup)
        const measureDuration = firstMeasure.Duration?.RealValue;
        const timeSignature = firstMeasure.ActiveTimeSignature;

        if (measureDuration && timeSignature) {
          const expectedDuration = timeSignature.RealValue;
          const isPartialMeasure = measureDuration < expectedDuration;

          if (isPartialMeasure) {
            pickupMeasureNumber = firstMeasure.MeasureNumber;
          }
        }
      }
    }

    // ========== DIAGNOSTIC LOGGING ==========
    console.log('\n🔍 ========== REPEAT STRUCTURE DIAGNOSTICS ==========');
    console.log(`Total measures in score: ${totalMeasures}`);
    console.log(`\n📍 DETECTED REPEAT MARKERS (${sortedRepeats.length}):`);
    sortedRepeats.forEach((r, i) => {
      console.log(`  ${i+1}. ${r.type} at measure ${r.measureNumber} (index: ${r.measureIndex})`);
    });
    console.log(`\n🎵 DETECTED ENDINGS (${sortedEndings.length}):`);
    sortedEndings.forEach((e, i) => {
      console.log(`  ${i+1}. Ending #${e.endingNumber} (${e.type}) at measure ${e.measureNumber} (index: ${e.measureIndex})`);
    });
    console.log(`\n📊 BUILT ENDING RANGES (${endingRanges.length}):`);
    endingRanges.forEach((range, i) => {
      console.log(`  ${i+1}. Ending #${range.endingNumber}: measures ${range.startMeasure}-${range.endMeasure}`);
    });

    // Show pickup detection results
    if (pickupMeasureNumber !== null) {
      console.log(`\n🎵 PICKUP MEASURE DETECTED:`);
      console.log(`   Measure ${pickupMeasureNumber} is a partial measure (pickup)`);
      console.log(`   Repeats will skip this measure and start at measure ${pickupMeasureNumber + 1}`);
    }

    // Check for forward repeat at measure 1 - this might indicate pickup issue
    const forwardRepeatAtOne = sortedRepeats.find(r => r.type === 'forward_repeat' && r.measureNumber === 1);
    if (forwardRepeatAtOne && pickupMeasureNumber === null) {
      console.log(`\n⚠️  POTENTIAL PICKUP NOTE WARNING:`);
      console.log(`   Forward repeat detected at measure 1 (index ${forwardRepeatAtOne.measureIndex})`);
      console.log(`   If there's a pickup note that wasn't detected, it will be included in repeats`);
    }
    console.log('\n');
    // ========== END DIAGNOSTIC LOGGING ==========

    // Build playback sequence with proper ending handling
    // Start at measure 1, but if there's a pickup at measure 0, we need to include it ONCE
    let currentMeasure = pickupMeasureNumber !== null ? pickupMeasureNumber : 1;
    const processedRepeats = new Set();
    let currentRepeatStart = null; // Track current repeat section
    let pickupPlayed = false; // Track if we've played the pickup measure

    console.log('🎯 BUILDING PLAYBACK ORDER (step-by-step):');
    console.log(`   Starting at measure ${currentMeasure}${pickupMeasureNumber !== null ? ' (pickup measure)' : ''}`);
    let stepNumber = 0;

    while (currentMeasure <= totalMeasures) {
      stepNumber++;

      // Check if there's a forward repeat at this measure
      const forwardRepeat = sortedRepeats.find(r =>
        r.type === 'forward_repeat' && r.measureNumber === currentMeasure
      );

      if (forwardRepeat) {
        console.log(`  Step ${stepNumber}: Measure ${currentMeasure} - FORWARD REPEAT detected, setting currentRepeatStart=${currentMeasure}`);
        currentRepeatStart = currentMeasure;
      }

      // Check if this measure is part of any ending (using ranges, not just markers)
      const measureEndingRanges = endingRanges.filter(range =>
        currentMeasure >= range.startMeasure && currentMeasure <= range.endMeasure
      );

      // Support any number of endings, not just 1 and 2
      const endingNumbers = measureEndingRanges.map(range => range.endingNumber);
      const isInEnding = endingNumbers.length > 0;

      if (isInEnding) {
        console.log(`  Step ${stepNumber}: Measure ${currentMeasure} - Inside ending(s): ${endingNumbers.map(n => `#${n}`).join(', ')}`);
      }

      // Check if there's a backward repeat at this measure
      const backwardRepeat = sortedRepeats.find(r =>
        r.type === 'backward_repeat' && r.measureNumber === currentMeasure
      );

      // If we encounter a backward repeat but no currentRepeatStart was set,
      // find the most recent forward repeat or default to measure 1 (or skip pickup if present)
      if (backwardRepeat && currentRepeatStart === null) {
        const mostRecentForwardRepeat = sortedRepeats
          .filter(r => r.type === 'forward_repeat' && r.measureNumber <= currentMeasure)
          .pop();
        let inferredStart = mostRecentForwardRepeat ? mostRecentForwardRepeat.measureNumber : 1;

        // If inferred start is the pickup measure, skip it
        if (pickupMeasureNumber !== null && inferredStart === pickupMeasureNumber) {
          inferredStart = pickupMeasureNumber + 1;
          console.log(`  Step ${stepNumber}: Measure ${currentMeasure} - BACKWARD REPEAT but no currentRepeatStart, inferring start=${inferredStart} (skipping pickup at ${pickupMeasureNumber})`);
        } else {
          console.log(`  Step ${stepNumber}: Measure ${currentMeasure} - BACKWARD REPEAT but no currentRepeatStart, inferring start=${inferredStart}`);
        }

        currentRepeatStart = inferredStart;
      }

      // Also set currentRepeatStart if we encounter an ending but no repeat start was set
      // This handles implicit repeats (no explicit forward repeat at beginning)
      if (isInEnding && currentRepeatStart === null) {
        const mostRecentForwardRepeat = sortedRepeats
          .filter(r => r.type === 'forward_repeat' && r.measureNumber <= currentMeasure)
          .pop();
        let inferredStart = mostRecentForwardRepeat ? mostRecentForwardRepeat.measureNumber : 1;

        // If inferred start is the pickup measure, skip it
        if (pickupMeasureNumber !== null && inferredStart === pickupMeasureNumber) {
          inferredStart = pickupMeasureNumber + 1;
          console.log(`  Step ${stepNumber}: Measure ${currentMeasure} - Ending found but no currentRepeatStart, inferring start=${inferredStart} (skipping pickup at ${pickupMeasureNumber})`);
        } else {
          console.log(`  Step ${stepNumber}: Measure ${currentMeasure} - Ending found but no currentRepeatStart, inferring start=${inferredStart}`);
        }

        currentRepeatStart = inferredStart;
      }

      // Determine if we should play this measure based on current repeat context
      let shouldPlay = true;
      const repeatKey = `repeat_${currentRepeatStart}`;
      const hasProcessedThisRepeat = processedRepeats.has(repeatKey);

      // NEW LOGIC: Support any number of endings based on ranges
      // Rule: On first pass (not processed), play ending #1, skip others
      //       On second pass (processed), skip ending #1, play ending #2
      //       On third pass, skip #1 and #2, play #3, etc.
      if (isInEnding) {
        // Check which ending number(s) this measure belongs to
        for (const endingNum of endingNumbers) {
          if (endingNum === 1 && hasProcessedThisRepeat) {
            // Skip ending #1 on second+ pass
            shouldPlay = false;
            console.log(`  Step ${stepNumber}: Measure ${currentMeasure} - SKIPPING (ending #${endingNum}, second+ pass, repeatKey=${repeatKey}, processed=${hasProcessedThisRepeat})`);
            break;
          } else if (endingNum > 1 && !hasProcessedThisRepeat) {
            // Skip ending #2+ on first pass
            shouldPlay = false;
            console.log(`  Step ${stepNumber}: Measure ${currentMeasure} - SKIPPING (ending #${endingNum}, first pass, repeatKey=${repeatKey}, processed=${hasProcessedThisRepeat})`);
            break;
          }
        }
      }

      if (shouldPlay) {
        playbackOrder.push(currentMeasure);

        // Mark pickup as played if this is the pickup measure
        if (pickupMeasureNumber !== null && currentMeasure === pickupMeasureNumber) {
          pickupPlayed = true;
          console.log(`  Step ${stepNumber}: Measure ${currentMeasure} - PLAYING PICKUP (repeatKey=${repeatKey}, processed=${hasProcessedThisRepeat}, playbackOrder length now: ${playbackOrder.length})`);
        } else {
          console.log(`  Step ${stepNumber}: Measure ${currentMeasure} - PLAYING (repeatKey=${repeatKey}, processed=${hasProcessedThisRepeat}, playbackOrder length now: ${playbackOrder.length})`);
        }
      }

      // Handle backward repeat
      if (backwardRepeat && !hasProcessedThisRepeat) {
        processedRepeats.add(repeatKey); // Mark this specific repeat as processed

        // Go back to the start of this repeat section
        let repeatStartMeasure = currentRepeatStart || 1;

        // PICKUP FIX: If pickup was played and we're jumping to a measure at or before it,
        // skip the pickup and jump to the next measure
        if (pickupMeasureNumber !== null && pickupPlayed && repeatStartMeasure <= pickupMeasureNumber) {
          repeatStartMeasure = pickupMeasureNumber + 1;
          console.log(`  Step ${stepNumber}: Measure ${currentMeasure} - BACKWARD REPEAT! Skipping pickup (measure ${pickupMeasureNumber}), jumping to measure ${repeatStartMeasure}, marking ${repeatKey} as processed`);
        } else {
          console.log(`  Step ${stepNumber}: Measure ${currentMeasure} - BACKWARD REPEAT! Jumping back to measure ${repeatStartMeasure}, marking ${repeatKey} as processed`);
        }

        currentMeasure = repeatStartMeasure;

        // Reset currentRepeatStart for potential next repeat section
        currentRepeatStart = null;
        continue;
      } else if (backwardRepeat && hasProcessedThisRepeat) {
        console.log(`  Step ${stepNumber}: Measure ${currentMeasure} - BACKWARD REPEAT already processed (${repeatKey}), continuing forward`);
      }

      currentMeasure++;
    }

    console.log(`\n✅ FINAL PLAYBACK ORDER: [${playbackOrder.join(', ')}]`);
    console.log(`   Total playback measures: ${playbackOrder.length} (original score has ${totalMeasures} measures)\n`);
    console.log('========== END DIAGNOSTICS ==========\n');

    return playbackOrder;
  }

  /**
   * Create expanded note events that follow the repeat structure
   * @param {Array} originalNotes - Original note events
   * @param {Array} playbackOrder - Measure playback order from buildPlaybackOrder
   * @param {Object} measureTimingMap - Map of measure numbers to timing info
   * @returns {Array} - Expanded note events in repeat order
   */
  static createExpandedNoteSequence(originalNotes, playbackOrder, measureTimingMap) {
    if (!playbackOrder || playbackOrder.length === 0) {
      return originalNotes;
    }

    const expandedNotes = [];
    let timeOffset = 0;

    // Group original notes by measure
    const notesByMeasure = new Map();
    for (const note of originalNotes) {
      const measureNum = this.findMeasureForNote(note, measureTimingMap);
      if (measureNum !== null && measureNum !== undefined) {
        if (!notesByMeasure.has(measureNum)) {
          notesByMeasure.set(measureNum, []);
        }
        notesByMeasure.get(measureNum).push(note);
      }
    }

    // PICKUP FIX: If playback order starts with measure 0 but there are no notes in measure 0,
    // it means the pickup notes are incorrectly assigned to measure 1
    // We need to split measure 1's notes into pickup (measure 0) and actual measure 1
    console.log(`\n🔍 PICKUP FIX CHECK:`);
    console.log(`   playbackOrder[0] = ${playbackOrder[0]}`);
    console.log(`   notesByMeasure.has(0) = ${notesByMeasure.has(0)}`);
    console.log(`   notesByMeasure.get(0)?.length = ${notesByMeasure.get(0)?.length}`);

    if (playbackOrder[0] === 0 && (!notesByMeasure.has(0) || notesByMeasure.get(0).length === 0)) {
      console.log(`   ✓ Condition met: playback starts with 0 but no notes in measure 0`);

      const measure1Timing = measureTimingMap.get(1);
      const measure0Timing = measureTimingMap.get(0);

      console.log(`   measure0Timing exists: ${!!measure0Timing}`);
      console.log(`   measure1Timing exists: ${!!measure1Timing}`);
      console.log(`   notesByMeasure.has(1): ${notesByMeasure.has(1)}`);

      if (measure1Timing && measure0Timing && notesByMeasure.has(1)) {
        console.log(`   ✓ All timing data exists, proceeding with note split`);

        const measure1Notes = notesByMeasure.get(1);
        const pickupNotes = [];
        const actualMeasure1Notes = [];

        // Split notes: notes starting before measure 1's actual start time are pickup notes
        const measure1ActualStart = measure0Timing.endTime; // Pickup ends where measure 1 begins

        console.log(`   Measure 0: ${measure0Timing.startTime} to ${measure0Timing.endTime}`);
        console.log(`   Measure 1 actual start: ${measure1ActualStart}`);
        console.log(`   Analyzing ${measure1Notes.length} notes in measure 1...`);

        for (const note of measure1Notes) {
          if (note.startTime < measure1ActualStart) {
            // This note starts in the pickup region
            pickupNotes.push(note);
            console.log(`     Note at ${note.startTime} → PICKUP (< ${measure1ActualStart})`);
          } else {
            // This note starts in actual measure 1
            actualMeasure1Notes.push(note);
          }
        }

        if (pickupNotes.length > 0) {
          console.log(`\n🔧 PICKUP NOTE FIX: Found ${pickupNotes.length} notes incorrectly assigned to measure 1`);
          console.log(`   Moving them to measure 0 (pickup starts at ${measure0Timing.startTime}, ends at ${measure1ActualStart})`);

          notesByMeasure.set(0, pickupNotes);
          notesByMeasure.set(1, actualMeasure1Notes);
        } else {
          console.log(`   ⚠️ No pickup notes found (all notes start at/after ${measure1ActualStart})`);
        }
      } else {
        console.log(`   ✗ Missing timing data or measure 1 notes`);
      }
    } else {
      console.log(`   ✗ Condition not met (either not starting with 0, or measure 0 already has notes)`);
    }

    // DIAGNOSTIC: Show how many notes are in each measure
    console.log('\n📝 NOTES GROUPED BY MEASURE:');
    const sortedMeasures = Array.from(notesByMeasure.keys()).sort((a, b) => a - b);
    sortedMeasures.slice(0, 5).forEach(measureNum => {
      const notes = notesByMeasure.get(measureNum);
      console.log(`  Measure ${measureNum}: ${notes.length} notes`);
    });
    if (sortedMeasures.length > 5) {
      console.log(`  ... and ${sortedMeasures.length - 5} more measures`);
    }
    console.log('');

    // Process measures in playback order
    for (let playbackIndex = 0; playbackIndex < playbackOrder.length; playbackIndex++) {
      const measureNum = playbackOrder[playbackIndex];
      const measureNotes = notesByMeasure.get(measureNum) || [];
      
      
      // Calculate measure duration (for timing offset)
      const measureTiming = measureTimingMap.get(measureNum);
      const measureDuration = measureTiming ? 
        (measureTiming.endTime - measureTiming.startTime) : 
        (measureNotes.length > 0 ? 
          Math.max(...measureNotes.map(n => n.endTime)) - Math.min(...measureNotes.map(n => n.startTime)) : 
          0);

      // Add notes for this measure occurrence
      for (const note of measureNotes) {
        // Calculate relative position within the measure
        const measureStartTime = measureTiming?.startTime || 0;
        const relativeStartTime = note.startTime - measureStartTime;
        const relativeEndTime = note.endTime - measureStartTime;
        
        // Ensure we don't create negative times
        const newStartTime = Math.max(0, timeOffset + relativeStartTime);
        const newEndTime = Math.max(newStartTime + 0.001, timeOffset + relativeEndTime);
        
        const expandedNote = {
          ...note,
          startTime: newStartTime,
          endTime: newEndTime,
          originalStartTime: note.startTime,
          originalEndTime: note.endTime,
          originalMeasure: measureNum,
          playbackIndex: playbackIndex,
          isRepeated: playbackOrder.slice(0, playbackIndex).includes(measureNum),
          // Debug info
          _debug: {
            measureStartTime,
            relativeStartTime,
            relativeEndTime,
            timeOffset,
            measureNum
          }
        };
        
        
        expandedNotes.push(expandedNote);
      }

      timeOffset += measureDuration;
    }

    const sortedNotes = expandedNotes.sort((a, b) => a.startTime - b.startTime);
    
    // Debug logging for repeat expansion issues
    if (playbackOrder.length > originalNotes.length) {
      console.log('🔍 REPEAT EXPANSION DEBUG:');
      console.log(`Original notes: ${originalNotes.length}, Expanded notes: ${sortedNotes.length}`);
      console.log(`Playback order: ${playbackOrder.join(' → ')}`);
      
      // Log first few notes of each repeated section
      const firstRepeatedMeasure = playbackOrder.find((measure, index) => 
        index > 0 && playbackOrder.slice(0, index).includes(measure)
      );
      
      if (firstRepeatedMeasure) {
        console.log(`First repeated measure: ${firstRepeatedMeasure}`);
        const firstRepeatNotes = sortedNotes.filter(note => 
          note.originalMeasure === firstRepeatedMeasure && note.isRepeated
        ).slice(0, 3);
        
        console.log('First 3 notes in repeated section:', firstRepeatNotes.map(n => ({
          pitch: n.pitch,
          startTime: n.startTime.toFixed(3),
          originalStartTime: n.originalStartTime?.toFixed(3),
          measureNum: n.originalMeasure,
          isRepeated: n.isRepeated,
          debug: n._debug
        })));
      }
    }
    
    return sortedNotes;
  }

  /**
   * Find which measure a note belongs to based on timing
   * @param {Object} note - Note event
   * @param {Map} measureTimingMap - Measure timing map
   * @returns {number|null} - Measure number or null
   */
  static findMeasureForNote(note, measureTimingMap) {
    // Use source note measure number if available (most reliable)
    if (note.sourceNote) {
      const sourceMeasure = this.extractMeasureNumberFromSourceNote(note.sourceNote);
      if (sourceMeasure) {
        return sourceMeasure;
      }
    }
    
    // First pass: try exact boundaries  
    for (const [measureNum, timing] of measureTimingMap.entries()) {
      if (note.startTime >= timing.startTime && note.startTime < timing.endTime) {
        return measureNum;
      }
    }
    
    // Second pass: if exact match fails, find closest measure (for boundary edge cases)
    let closestMeasure = null;
    let closestDistance = Infinity;
    
    for (const [measureNum, timing] of measureTimingMap.entries()) {
      const midPoint = (timing.startTime + timing.endTime) / 2;
      const distance = Math.abs(note.startTime - midPoint);
      
      if (distance < closestDistance) {
        closestDistance = distance;
        closestMeasure = measureNum;
      }
    }
    
    
    return closestMeasure;
  }
  
  /**
   * Extract measure number from source note - helper method
   */
  static extractMeasureNumberFromSourceNote(sourceNote) {
    try {
      if (sourceNote.sourceMeasure?.MeasureNumber) {
        return sourceNote.sourceMeasure.MeasureNumber;
      }
      
      if (sourceNote.parentStaffEntry?.ParentMeasure?.MeasureNumber) {
        return sourceNote.parentStaffEntry.ParentMeasure.MeasureNumber;
      }
      
      if (sourceNote.ParentVoiceEntry?.ParentStaffEntry?.ParentMeasure?.MeasureNumber) {
        return sourceNote.ParentVoiceEntry.ParentStaffEntry.ParentMeasure.MeasureNumber;
      }
      
      return null;
    } catch (error) {
      return null;
    }
  }

  /**
   * Extract repeat information from OSMD's parsed data structures
   * @param {Object} osmd - OSMD instance
   * @returns {Object} - Repeat analysis result
   */
  static extractRepeatsFromOSMD(osmd) {
    try {
      if (!osmd?.Sheet?.sourceMeasures) {
        return { hasRepeats: false };
      }

      const repeatStructures = [];
      const endings = [];
      const measures = osmd.Sheet.sourceMeasures;

      for (let i = 0; i < measures.length; i++) {
        const measure = measures[i];
        const measureNumber = measure.MeasureNumber || i + 1;

        // Check for repeat instructions
        if (measure.firstRepetitionInstructions?.length > 0) {
          for (const instruction of measure.firstRepetitionInstructions) {
            if (instruction.type === 0) { // Forward repeat
              repeatStructures.push({
                type: 'forward_repeat',
                measureNumber: measureNumber,
                measureIndex: i
              });
            } else if (instruction.type === 3) { // Ending
              const endingNumber = instruction.endingIndices?.[0] || 1;
              endings.push({
                endingNumber: endingNumber,
                type: 'start',
                measureNumber: measureNumber,
                measureIndex: i
              });
            }
          }
        }

        if (measure.lastRepetitionInstructions?.length > 0) {
          for (const instruction of measure.lastRepetitionInstructions) {
            if (instruction.type === 2) { // Backward repeat
              repeatStructures.push({
                type: 'backward_repeat',
                measureNumber: measureNumber,
                measureIndex: i
              });
            } else if (instruction.type === 3) { // Ending
              const endingNumber = instruction.endingIndices?.[0] || 1;
              endings.push({
                endingNumber: endingNumber,
                type: 'stop',
                measureNumber: measureNumber,
                measureIndex: i
              });
            }
          }
        }

        // Check for endings (might be in different properties)
        if (measure.endingElements?.length > 0) {
          for (const ending of measure.endingElements) {
            endings.push({
              endingNumber: ending.number || 1,
              type: ending.type || 'start',
              measureNumber: measureNumber,
              measureIndex: i
            });
          }
        }
      }

      if (repeatStructures.length > 0 || endings.length > 0) {
        const playbackOrder = this.buildPlaybackOrder(measures.length, repeatStructures, endings, osmd);


        return {
          hasRepeats: true,
          playbackOrder: playbackOrder,
          repeatStructures: repeatStructures,
          endings: endings,
          totalMeasures: measures.length
        };
      }
      
      return { hasRepeats: false };
    } catch (error) {
      return { hasRepeats: false };
    }
  }

  /**
   * Parse raw MusicXML for repeat information
   * @param {Object} osmd - OSMD instance
   * @returns {Object} - Repeat analysis result
   */
  static parseRawMusicXML(osmd) {
    try {
      // Try multiple sources for XML content
      const xmlFromOsmd = osmd?._xmlString || osmd?.Sheet?._xmlString;
      const xmlFromWindow = window._currentMusicXMLContent;
      
      
      // Prefer OSMD's XML first, then fall back to window content
      const xmlString = xmlFromOsmd || xmlFromWindow;
      
      if (!xmlString) {
        return { hasRepeats: false };
      }
      

      // Parse XML string - ensure proper XML parsing
      const parser = new DOMParser();
      
      // Clean the XML string and ensure proper encoding
      let cleanXmlString = xmlString;
      
      // If the string starts with a BOM, remove it
      if (cleanXmlString.charCodeAt(0) === 0xFEFF) {
        cleanXmlString = cleanXmlString.slice(1);
      }
      
      // Remove any null characters that might cause parsing issues
      cleanXmlString = cleanXmlString.replace(/\0/g, '');
      
      // Try to parse as XML
      let xmlDoc = parser.parseFromString(cleanXmlString, 'application/xml');
      
      // Check if parsing failed (DOMParser returns HTML on XML parse errors)
      if (xmlDoc.documentElement.tagName === 'html' || xmlDoc.querySelector('parsererror')) {
        // Remove DOCTYPE declaration which might be causing issues
        const withoutDoctype = cleanXmlString.replace(/<!DOCTYPE[^>]*>/gi, '');
        xmlDoc = parser.parseFromString(withoutDoctype, 'application/xml');
        
        // If still failing, try text/xml
        if (xmlDoc.documentElement.tagName === 'html' || xmlDoc.querySelector('parsererror')) {
          xmlDoc = parser.parseFromString(withoutDoctype, 'text/xml');
        }
      }
      
      const measures = xmlDoc.querySelectorAll('measure');
      
      const repeatStructures = [];
      const endings = [];
      
      
      measures.forEach((measure, index) => {
        const measureNumber = parseInt(measure.getAttribute('number')) || index + 1;
        
        
        // Check for forward repeats - try multiple patterns
        let forwardRepeats = measure.querySelectorAll('barline repeat[direction="forward"]');
        if (forwardRepeats.length === 0) {
          // Try alternate patterns
          forwardRepeats = measure.querySelectorAll('repeat[direction="forward"]');
        }
        if (forwardRepeats.length === 0) {
          // Try without direction attribute
          const allRepeats = measure.querySelectorAll('repeat');
          allRepeats.forEach(repeat => {
            const direction = repeat.getAttribute('direction');
            if (direction === 'forward' || direction === 'start') {
              forwardRepeats = [repeat];
            }
          });
        }
        
        
        forwardRepeats.forEach(() => {
          repeatStructures.push({
            type: 'forward_repeat',
            measureNumber: measureNumber,
            measureIndex: index
          });
        });
        
        // Check for backward repeats - try multiple patterns
        let backwardRepeats = measure.querySelectorAll('barline repeat[direction="backward"]');
        if (backwardRepeats.length === 0) {
          backwardRepeats = measure.querySelectorAll('repeat[direction="backward"]');
        }
        if (backwardRepeats.length === 0) {
          const allRepeats = measure.querySelectorAll('repeat');
          allRepeats.forEach(repeat => {
            const direction = repeat.getAttribute('direction');
            if (direction === 'backward' || direction === 'end') {
              backwardRepeats = [repeat];
            }
          });
        }
        
        
        backwardRepeats.forEach(() => {
          repeatStructures.push({
            type: 'backward_repeat',
            measureNumber: measureNumber,
            measureIndex: index
          });
        });
        
        // Check for endings - try multiple patterns
        let endingElements = measure.querySelectorAll('barline ending');
        if (endingElements.length === 0) {
          endingElements = measure.querySelectorAll('ending');
        }
        
        
        endingElements.forEach(ending => {
          const number = parseInt(ending.getAttribute('number')) || 1;
          const type = ending.getAttribute('type') || 'start';
          
          endings.push({
            endingNumber: number,
            type: type,
            measureNumber: measureNumber,
            measureIndex: index
          });
        });
      });
      
      if (repeatStructures.length > 0 || endings.length > 0) {
        const playbackOrder = this.buildPlaybackOrder(measures.length, repeatStructures, endings, osmd);

        return {
          hasRepeats: true,
          playbackOrder: playbackOrder,
          repeatStructures: repeatStructures,
          endings: endings,
          totalMeasures: measures.length
        };
      }
      
      return { hasRepeats: false };
    } catch (error) {
      return { hasRepeats: false };
    }
  }

  /**
   * Debug function to log repeat analysis results
   * @param {Object} analysis - Result from analyzeRepeats
   */
  static debugRepeatAnalysis(analysis) {
    console.log('🔄 REPEAT ANALYSIS RESULTS:');
    console.log('==========================================');
    console.log(`Has repeats: ${analysis.hasRepeats}`);
    console.log(`Total measures: ${analysis.totalMeasures}`);
    
    if (analysis.repeatStructures.length > 0) {
      console.log('\n📍 REPEAT STRUCTURES:');
      analysis.repeatStructures.forEach((repeat, index) => {
        console.log(`${index + 1}. ${repeat.type} at measure ${repeat.measureNumber}`);
      });
    }
    
    if (analysis.endings.length > 0) {
      console.log('\n🎵 ENDINGS:');
      analysis.endings.forEach((ending, index) => {
        console.log(`${index + 1}. Ending ${ending.endingNumber} (${ending.type}) at measure ${ending.measureNumber}`);
      });
    }
    
    if (analysis.playbackOrder) {
      console.log('\n▶️ PLAYBACK ORDER:');
      console.log(`Measures: ${analysis.playbackOrder.join(' → ')}`);
      console.log(`Total playback measures: ${analysis.playbackOrder.length}`);
    }
    
    console.log('==========================================');
  }
}